import axios from '@/plugins/axios.js';

// Statuses a retry can never fix: the server made a deterministic decision
// (bad payload, no session, forbidden, gone, validation). Retrying these only
// hides the failure from the user for minutes while the UI sits disabled.
// 419 is the exception — the failed response carries a refreshed CSRF cookie,
// so a single retry either succeeds or converts into a 401 that flows into the
// global session-expired handling.
const NON_RETRYABLE_STATUSES = [400, 401, 403, 404, 409, 422];

export default class RetryRequest {
    constructor({ question, message }) {
        this.ids = [];
        this.question = question;
        this.promptMessage = message;
    }

    delay(delayCount) {
        return new Promise(resolve => {
            const timeout = setTimeout(resolve, delayCount);

            this.ids.push(timeout);
        });
    }

    shouldRetry(error, retryCount) {
        const status = error?.response?.status;

        // No response at all: network blip / offline — retrying is the point.
        if (status === undefined) return true;

        if (status === 419) return retryCount < 1;

        if (NON_RETRYABLE_STATUSES.includes(status)) return false;

        // 429 (throttled) and 5xx are transient by definition.
        return status === 429 || status >= 500;
    }

    async request({ action, retryCount = 0, retryLimit = 5, delayCount = 5000, lastError = null, onFailure = null }) {
        if (retryCount > retryLimit) throw lastError ?? new Error('Retry limit exceeded');

        return action()
            .then(resp => {
                this._success = true;
                return resp;
            })
            .catch(async error => {
                // Check if the error is due to request cancellation
                if (axios.isCancel(error)) {
                    // Don't retry cancelled requests, just throw the error silently
                    throw error;
                }

                if (!this.shouldRetry(error, retryCount)) {
                    throw error;
                }

                // Surface the miss to the caller on the FIRST failure so its recovery
                // affordance (the inline "Your answer could not be saved." + Retry)
                // appears immediately, instead of only after the whole retry window
                // elapses. Retries then keep running quietly in the background; a
                // silent success lets the caller clear the affordance.
                if (retryCount === 0 && typeof onFailure === 'function') {
                    onFailure(error);
                }

                await this.delay(delayCount);

                return this.request({
                    action,
                    retryCount: retryCount + 1,
                    retryLimit,
                    lastError: error,
                    delayCount,
                    onFailure,
                });
            })
            .finally(() => {
                if (this._success) return;

                if (this.ids.length) {
                    this.ids.forEach(id => clearTimeout(id));

                    this.ids = [];
                }
            });
    }
}
